import { createFileRoute, Link } from "@tanstack/react-router";
import { FileSearch, ListChecks, Quote, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SiteHeader } from "@/components/SiteHeader";
const FAQS = [
  {
    q: "What does the SmartEssay essay checker do?",
    a: "The essay checker reviews your draft for evidence gaps, paragraph-level flow, and prioritized improvements. Where a claim is missing support, it can suggest real word-for-word quotes with MLA or APA 7 citations from the work you cite.",
  },
  {
    q: "Is the essay checker free?",
    a: "Yes — you can try the Essay Reviewer free without signing up. Paid plans unlock unlimited reviews, larger uploads, and PDF export.",
  },
  {
    q: "Does the essay checker catch grammar mistakes?",
    a: "It focuses on structure, evidence, and flow — the things that actually move a grade. For sentence-level grammar, pair it with a dedicated proofreader; for argument and citation quality, the essay checker is built for exactly that.",
  },
  {
    q: "Can I upload a PDF or a Word doc?",
    a: "Yes. Upload a PDF, DOCX, or paste your draft directly. You can also drag in a voice or video source and we'll transcribe it.",
  },
  {
    q: "Will my essay be saved or shared?",
    a: "Drafts you paste for review are processed for the review and not shared with anyone. See our privacy page for full details.",
  },
];

export const Route = createFileRoute("/essay-checker")({
  head: () => ({
    meta: [
      { title: "Free AI Essay Checker — Evidence, Flow & Citations" },
      { name: "description", content: "A free AI essay checker for students. Upload or paste your draft and get evidence gaps with real cited quotes, paragraph flow analysis, and prioritized fixes." },
      { property: "og:title", content: "Free AI Essay Checker — SmartEssay" },
      { property: "og:description", content: "Evidence gaps with real cited quotes, flow analysis, and prioritized fixes — for free." },
      { property: "og:url", content: "https://smartessay.ai/essay-checker" },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/essay-checker" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }],
  }),
  component: EssayCheckerPage,
});

function EssayCheckerPage() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-6 pb-24">
        <header className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Essay checker</p>
          <h1 className="mt-3 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            A free <em>essay checker</em> built for students
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Paste or upload your draft and our essay checker shows you exactly what to fix: evidence gaps with real cited quotes, a paragraph-by-paragraph flow map, and prioritized improvements. It catches what teachers actually grade for.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="shadow-glow">
              <Link to="/reviewer" search={{ try: "1" }}>Check my essay</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/help">How it works</Link>
            </Button>
          </div>
        </header>

        <section className="mt-14 grid gap-6 sm:grid-cols-3">
          {[
            { icon: FileSearch, title: "Evidence gaps", body: "Find the claims that need support and the spots where evidence is thin." },
            { icon: Quote, title: "Suggested quotes", body: "Real word-for-word quotes with MLA or APA 7 citations from the work you cite." },
            { icon: ListChecks, title: "Flow & priorities", body: "Paragraph-by-paragraph flow analysis and an ordered fix-list." },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <f.icon className="h-5 w-5 text-primary" />
              <h2 className="mt-3 font-display text-base font-semibold">{f.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-medium">Essay checker — common questions</h2>
          <Accordion type="single" collapsible className="mt-4">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`}>
                <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="mt-14 rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
          <ShieldCheck className="mx-auto h-6 w-6 text-primary" />
          <h2 className="mt-3 font-display text-xl font-semibold">Check your draft in seconds</h2>
          <p className="mt-2 text-sm text-muted-foreground">No card needed for your first review.</p>
          <Button asChild className="mt-5 shadow-glow">
            <Link to="/reviewer" search={{ try: "1" }}>Start the essay checker</Link>
          </Button>
        </section>
      </main>
    </div>
  );
}
